import { reasonApi } from '@/main/api';
import { KeyValue } from '@/main/models/common';
import { KeyValueDefaultEnum } from '@/main/models/common/keyValueDefaultEnum';
import { ReasonFilterRequest, ReasonRequest } from '@/main/models/request';
import { ClaimItemsToRequoteResponse, RequoteProcurementSupplierResponse } from '@/main/models/response';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { getTranslateByKey, isNullUndefined } from '@/main/services/utils';

export interface ReasonsClaim extends KeyValueDefaultEnum {
  idsReasonType: number[];
}

function mergeUniqueEnumAsKey(
  quote: ReasonRequest[],
  procurement: ReasonRequest[]
): KeyValueDefaultEnum[] {
  const seenEnums = new Set<number>();
  const combined: KeyValueDefaultEnum[] = [];

  [...quote, ...procurement].forEach(item => {
    if (!seenEnums.has(item.enum)) {
      seenEnums.add(item.enum);
      combined.push({
        key: item.enum,
        value: item.value,
        default: item.default,
        enum: item.enum
      });
    }
  });

  return combined;
}

function addIdsReasonTypeInReason(
  reasonsMerged: KeyValueDefaultEnum[],
  reasonQuote: ReasonRequest[],
  reasonProcurement: ReasonRequest[]
): ReasonsClaim[] {
  const result = reasonsMerged.map(reason => {
    const hasReasonQuote = reasonQuote.find(item => item.enum === reason.enum);
    const hasReasonProcurement = reasonProcurement.find(
      item => item.enum === reason.enum
    );

    return {
      ...reason,
      idsReasonType: [
        ...(hasReasonQuote ? [ReasonTypeEnum.RequoteQuote] : []),
        ...(hasReasonProcurement ? [ReasonTypeEnum.RequoteProcurement] : [])
      ]
    };
  });

  return result;
}

export interface IGetReasonOptionClaimResponse {
  quote: ReasonRequest[];
  procurement: ReasonRequest[];
  mergedReasons: ReasonsClaim[];
}
export async function getReasonOptionClaim(
  guidCountry: string
): Promise<IGetReasonOptionClaimResponse> {
  try {
    const requestReasonQuote: ReasonFilterRequest = {
      idReasonType: ReasonTypeEnum.RequoteQuote,
      guidCountry
    };
    const requestReasonProcurement: ReasonFilterRequest = {
      idReasonType: ReasonTypeEnum.RequoteProcurement,
      guidCountry
    };

    const [responseQuote, responseProcurement] = await Promise.all([
      reasonApi.getDomain(requestReasonQuote),
      reasonApi.getDomain(requestReasonProcurement)
    ]);
    const quote = responseQuote.data.result;
    const procurement = responseProcurement.data.result;

    const mergedReasons = mergeUniqueEnumAsKey(quote, procurement);
    const mergedReasonWithIdsReasonType: ReasonsClaim[] = addIdsReasonTypeInReason(
      mergedReasons,
      quote,
      procurement
    );
    return {
      quote,
      procurement,
      mergedReasons: mergedReasonWithIdsReasonType
    };
  } catch (error) {
    console.error('Error fetching reason options:', error);
    return {
      quote: [],
      procurement: [],
      mergedReasons: []
    };
  }
}

export function getReasonByEnumReasonType(
  reasons: IGetReasonOptionClaimResponse | null,
  reasonEnum: number,
  reasonType: number
) {
  if (isNullUndefined(reasons)) return '';

  if (reasonType === ReasonTypeEnum.RequoteQuote) {
    const reason = reasons.quote.find(item => item.enum === reasonEnum);
    return isNullUndefined(reason) ? '' : String(reason.key);
  }

  if (reasonType === ReasonTypeEnum.RequoteProcurement) {
    const reason = reasons.procurement.find(item => item.enum === reasonEnum);
    return isNullUndefined(reason) ? '' : String(reason.key);
  }

  const reason = reasons.mergedReasons.find(
    item => item.enum === reasonEnum && item.idsReasonType.includes(reasonType)
  );
  return !isNullUndefined(reason) ? String(reason.key) : '';
}

export function agroupPartByNameSupplier(data:ClaimItemsToRequoteResponse): ClaimItemsToRequoteResponse{

  if (isNullUndefined(data)) return data;
  if (isNullUndefined(data.suppliers)) return data;
  const result:ClaimItemsToRequoteResponse = {
    ...data,
    suppliers: data.suppliers.reduce((acc: RequoteProcurementSupplierResponse[], supplier) => {
      const existingSupplier = acc.find(item => item.supplierName === supplier.supplierName);
      if (existingSupplier) {
        existingSupplier.items.push(...supplier.items);
      } else {
        acc.push({
          ...supplier,
          items: [...supplier.items]
        });
      }
      return acc;
    }
    , [])
  };

  return result;
}

export function orderByName(data:ClaimItemsToRequoteResponse): ClaimItemsToRequoteResponse{
  if (isNullUndefined(data)) return data;
  if (isNullUndefined(data.suppliers)) return data;
  const result:ClaimItemsToRequoteResponse = {
    ...data,
    unansweredQuoteItems: data.unansweredQuoteItems.sort((a, b) => (a.quoteProcurementPartInfo?.quoteItemDescription ?? '').localeCompare((b.quoteProcurementPartInfo?.quoteItemDescription ?? ''))),
    abovePriceCapQuoteItems: data.abovePriceCapQuoteItems.sort((a, b) => (a.quoteProcurementPartInfo?.quoteItemDescription ?? '').localeCompare((b.quoteProcurementPartInfo?.quoteItemDescription ?? ''))),
    suppliers: data.suppliers.map((supplier) => ({
      ...supplier,
      items: supplier.items.sort((a, b) => (a.quoteProcurementPartInfo?.partName ?? '').localeCompare((b.quoteProcurementPartInfo?.partName ?? '')))
    }))
  };
  return result;
}